import type { ReportValidationCheck, ReportValidationLevel } from '@/core/models/reports.models';
import { Button } from '@/kit/Button';
import { DialogActions, DialogContent, type DialogComponentProps } from '@/kit/dialog';

export interface ReportValidationDialogData {
  checks: ReportValidationCheck[];
}

/** BUG-047: the check's level as the report prints it; an API without levels sends Blocking or Warning by isBlocking. */
const levelOf = (check: ReportValidationCheck): ReportValidationLevel =>
  check.level ?? (check.isBlocking ? 'Blocking' : 'Warning');

/** The dialog options Add New Report opens it with. */
export const REPORT_VALIDATION_DIALOG = { width: '700px', maxWidth: '95vw' } as const;

/**
 * The pre-Generate validation gate: shown before the PDF preview opens, whenever the validation finds anything to flag.
 * BUG-047: it lists every check - warnings, errors and blocking issues - each with its type, as Capium 3.0's validation
 * status report did. As in 3.0 (Accounts/Reports/Default.js verifyReport) every row - blocking ones included - is
 * skippable: "Skip & Preview Report" is always available and the preview and Save Report follow. The hard stops are at
 * Submit, not here. Closes with true (Skip & Preview Report) or false (Close).
 */
export function ReportValidationDialog({ data, dialogRef }: DialogComponentProps<ReportValidationDialogData, boolean>) {
  const checks = data?.checks ?? [];
  const hasBlocking = checks.some((c) => c.isBlocking);

  return (
    <>
      <h2 className="mat-mdc-dialog-title mdc-dialog__title">Validation status report</h2>

      <DialogContent>
        <table className="mat-table wp-100">
          <thead>
            <tr>
              <th className="p-1 text-align-left">S.No</th>
              <th className="p-1 text-align-left">Message</th>
              <th className="p-1 text-align-left">Type</th>
            </tr>
          </thead>
          <tbody>
            {checks.map((check, index) => (
              <tr key={index}>
                <td className="p-1">{index + 1}</td>
                <td className="p-1">{check.message}</td>
                <td className="p-1">
                  <span className={levelOf(check) === 'Warning' ? 'text-yellow-100' : 'text-danger-100'}>
                    {levelOf(check)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {hasBlocking && (
          <p className="font-size-14 mt-1">
            Blocking issues must be fixed before these accounts can be submitted. You can still preview and save the
            report now.
          </p>
        )}
      </DialogContent>

      <DialogActions style={{ justifyContent: 'flex-end', gap: '12px' }}>
        <Button className="action-button secondary-button" onClick={() => dialogRef.close(false)}>
          Close
        </Button>
        <Button className="action-button primary-button" onClick={() => dialogRef.close(true)}>
          Skip &amp; Preview Report
        </Button>
      </DialogActions>
    </>
  );
}
